"use client";
import line from "@/assets/landingPage/icon/line-white.svg";
import { useSettings } from "@/context/settingsContext";
import ApiPath from "@/network/api/apiPath";
import NetworkService from "@/network/service/networkService";
import { getSettingValue, normalizeHref } from "@/utils/utils";
import Cookies from "js-cookie";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";

const Footer = () => {
  const network = new NetworkService();
  const [navigation, setNavigation] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const { settings } = useSettings();
  const siteLogo = getSettingValue(settings, "site_logo");
  const siteLogoHeight = getSettingValue(settings, "site_logo_height");

  const fetchNavigation = async () => {
    try {
      // Get current locale from cookie
      const locale = Cookies.get("NEXT_LOCALE") || "en";

      // Add locale as query parameter
      const res = await network.globalGet(`${ApiPath.navigation}?locale=${locale}`);
      if (res?.status === "completed") {
        setNavigation(res?.data?.data || {});
      } else {
        setNavigation({});
      }
    } catch (error) {
      console.error("Failed to fetch footer navigation:", error);
      setNavigation({});
    } finally {
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      toast.error("Email is required");
      return;
    }
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    setLoading(true);

    try {
      const res = await network.post(ApiPath.subscribe, { email });
      if (res?.status === "completed") {
        toast.success(res?.data?.message);
        setEmail("");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNavigation();
  }, []);

  // Refetch navigation when language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      fetchNavigation();
    };

    window.addEventListener("languageChange", handleLanguageChange);
    return () => window.removeEventListener("languageChange", handleLanguageChange);
  }, []);

  return (
    <footer className="relative pt-[50px] lg:pt-[80px] xl:pt-[100px] pb-7.5 bg-[#2D2D2D]">
      <div className="custom-container mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-7 gap-8 lg:gap-12">
          <div className="lg:col-span-2 2xl:col-span-3">
            <div className="mb-3 lg:mb-4">
              <Link
                href="/"
                className={`logo h-[25px] sm:h-[${siteLogoHeight}px] w-auto block`}
              >
                {siteLogo ? (
                  <Image
                    src={siteLogo}
                    alt="Logo"
                    width={100}
                    height={100}
                    unoptimized
                    className="h-full w-auto"
                  />
                ) : (
                  <div className="h-[30px] w-[100px] bg-gray-100 animate-pulse rounded"></div>
                )}
              </Link>
            </div>
            <p className="text-[rgba(255,255,255,0.60)] text-[15px] font-medium mb-5 lg:mb-10 max-w-sm">
              {navigation?.footer?.newsletter?.title}
            </p>
            <div className="mb-4 lg:mb-5">
              <form
                className="relative max-w-[355px]"
                onSubmit={handleSubscribe}
              >
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="backdrop-blur-[2px] w-full h-[52px] text-[14px] text-white font-medium px-4 py-2.5 rounded-[18px] border border-[rgba(255,255,255,0.16)] focus:outline-none focus:ring-0 focus:ring-transparent focus:border-user-primary bg-[rgba(255,255,255,0.04)] placeholder-[rgba(255,255,255,0.60)] pe-[110px]"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <button
                  type="submit"
                  className="h-[34px] px-[10px] bg-user-primary text-white rounded-[10px] text-[14px] font-semibold absolute rtl:left-[8px] ltr:right-[8px] top-1/2 -translate-y-1/2"
                  disabled={loading}
                >
                  {loading ? "Subscribing..." : "Subscribe"}
                </button>
              </form>
            </div>

            <p className="text-[15px] text-[rgba(255,255,255,0.60)] font-medium max-w-sm">
              {navigation?.footer?.newsletter?.description}
            </p>
          </div>

          <div>
            <h3 className="text-white font-bold mb-3 lg:mb-5 text-base">
              {navigation?.footer?.menus?.widget_one?.title}
            </h3>
            <ul className="space-y-3 lg:space-y-5">
              {navigation?.footer?.menus?.widget_one?.data?.map((link) => (
                <li key={link.name}>
                  <a
                    href={normalizeHref(link.slug)}
                    className="text-[rgba(255,255,255,0.60)] hover:text-white transition-colors duration-200 text-sm font-medium"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-white font-bold mb-3 lg:mb-5 text-base">
              {navigation?.footer?.menus?.widget_two?.title}
            </h3>
            <ul className="space-y-3 lg:space-y-5">
              {navigation?.footer?.menus?.widget_two?.data?.map((link) => (
                <li key={link.name}>
                  <a
                    href={normalizeHref(link.slug)}
                    className="text-[rgba(255,255,255,0.60)] hover:text-white transition-colors duration-200 text-sm font-medium"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-white font-bold mb-3 lg:mb-5 text-base">
              {navigation?.footer?.menus?.widget_three?.title}
            </h3>
            <ul className="space-y-3 lg:space-y-5">
              {navigation?.footer?.menus?.widget_three?.data?.map((link) => (
                <li key={link.name}>
                  <a
                    href={normalizeHref(link.slug)}
                    className="text-[rgba(255,255,255,0.60)] hover:text-white transition-colors duration-200 text-sm font-medium"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-white font-bold mb-3 lg:mb-5 text-base">
              Social Link
            </h3>
            <div className="flex space-x-3">
              {navigation?.footer?.socials?.map((link) => (
                <a
                  key={link?.url}
                  href={link?.url}
                  className="bg-[rgba(255,255,255,0.10)] border border-[rgba(116,69,255,0.10)] rounded-[8px] h-[40px] w-[40px] flex justify-center items-center group shrink-0"
                  target="_blank"
                >
                  <Image
                    src={link?.icon}
                    alt="link"
                    width={20}
                    height={20}
                    unoptimized
                    className="w-[20px] h-[20px]"
                  />
                </a>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-[16px] flex justify-center items-center">
          <div className="flex justify-center items-center">
            <h2 className="text-[60px] sm:text-[100px] lg:text-[150px] 2xl:text-[200px] font-semibold text-gradient-color uppercase">
              {navigation?.footer?.bottom_text}
            </h2>
          </div>
        </div>
        <div className="custom-container mx-auto mb-[30px]">
          <Image src={line} alt="line" className="w-full h-[1px]" />
        </div>
        <div className="">
          <p className="text-center text-[rgba(255,255,255,0.80)] text-sm font-semibold">
            {navigation?.footer?.copyright_text}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
